// File: frontend/src/components/admin/AdminEntryModals.tsx
// Extracted modal components for AdminEntries.tsx
import React, { RefObject } from "react";
import { useTranslation } from "react-i18next";
import {
  FaTimes,
  FaCommentAlt,
  FaExclamationTriangle,
  FaChevronUp,
  FaChevronDown,
  FaStar,
} from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { AdminEntryDto } from "../../types/adminEntry.types";
import { EntryEditorToolbar } from "../entries/EntryEditorToolbar";
import { renderEntryMarkup } from "../../utils/markupUtils";
import { formatAdminDate } from "../../utils/adminUtils";
import { ADMIN_REASON_MAX_LENGTH } from "../../constants";
// Entry length rules come from the admin panel, not from a compile-time constant
import { useSiteSettings } from "../../context/SiteSettingsContext";
// Shared "current / max" counter shown under every free-text modal field
import { AdminCharCounter } from "./common";
import "../entries/EntryPostForm.css";

// ─── Reason Modal ─────────────────────────────────────────────────────────────

interface ReasonModalProps {
  text: string;
  onClose: () => void;
}

export const ReasonModal: React.FC<ReasonModalProps> = ({ text, onClose }) => {
  const { t } = useTranslation();
  return (
    <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="admin-modal reason-modal">
        <div className="admin-modal-header">
          <h3><FaCommentAlt /> {t("admin_entry_reason_modal_title")}</h3>
          <button className="modal-close-btn" onClick={onClose}><FaTimes /></button>
        </div>
        <div className="admin-modal-body">
          <div className="reason-modal-content">{text}</div>
        </div>
        <div className="admin-modal-footer">
          <button className="modal-cancel-btn" onClick={onClose}>{t("admin_close")}</button>
        </div>
      </div>
    </div>
  );
};

// ─── Create Modal ─────────────────────────────────────────────────────────────

interface CreateModalProps {
  matchId: string;
  setMatchId: (v: string) => void;
  content: string;
  setContent: (v: string) => void;
  preview: boolean;
  setPreview: (v: boolean) => void;
  draftRestored: boolean;
  onDiscardDraft: () => void;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  error: string | null;
  onClose: () => void;
  onSubmit: () => void;
}

export const CreateEntryModal: React.FC<CreateModalProps> = ({
  matchId, setMatchId, content, setContent,
  preview, setPreview, draftRestored, onDiscardDraft,
  textareaRef, error, onClose, onSubmit,
}) => {
  const { t } = useTranslation();
  // Admin-configurable maximum entry length — same rule the backend enforces
  const { entryMaxLength } = useSiteSettings();
  return (
    <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="admin-modal entry-create-modal">
        <div className="admin-modal-header">
          <h3>{t("admin_entry_create_title")}</h3>
          <button className="modal-close-btn" onClick={onClose}><FaTimes /></button>
        </div>
        <div className="admin-modal-body">
          {error && <div className="modal-error">{error}</div>}
          <div className="entry-form-group">
            <label>{t("admin_entry_form_match_id")} <span className="required-star">*</span></label>
            <input
              type="number"
              placeholder={t("admin_entry_form_match_id_hint")}
              value={matchId}
              onChange={(e) => setMatchId(e.target.value)}
            />
          </div>
          <div className="entry-form-group">
            <label>{t("admin_entry_form_content")} <span className="required-star">*</span></label>
            {draftRestored && (
              <div className="epf-draft-notice">
                {t("admin_entry_draft_restored")}{" "}
                <button type="button" className="epf-draft-discard" onClick={onDiscardDraft}>
                  {t("admin_entry_draft_discard")}
                </button>
              </div>
            )}
            <div className="epf-toolbar admin-epf-toolbar">
              <EntryEditorToolbar textareaRef={textareaRef} content={content} setContent={setContent} />
              <span className="epf-char-counter">{content.length} / {entryMaxLength}</span>
            </div>
            {preview && (
              <div className="epf-preview-inline">
                <div className="epf-preview-inline-label">{t("admin_entry_preview_label")}</div>
                <div className="epf-preview-body">{renderEntryMarkup(content)}</div>
              </div>
            )}
            <textarea
              ref={textareaRef}
              className="entry-edit-textarea admin-epf-textarea"
              placeholder={t("admin_entry_form_content_hint")}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={6}
              maxLength={entryMaxLength}
            />
            <div className="admin-epf-bottom-row">
              <div className="epf-keyboard-wrapper">
                {content.length > 0 && (
                  <button type="button" className="epf-clear-btn" title={t("admin_entry_clear")}
                    onClick={() => { setContent(""); setPreview(false); }}>
                    <RiDeleteBin6Line size={16} />
                  </button>
                )}
              </div>
              <button type="button" className="epf-preview-btn"
                onClick={() => setPreview(!preview)} disabled={!content.trim()}>
                {t("admin_entry_preview_btn")}
              </button>
            </div>
          </div>
        </div>
        <div className="admin-modal-footer">
          <button className="modal-cancel-btn" onClick={onClose}>{t("admin_cancel")}</button>
          <button className="modal-confirm-btn" onClick={onSubmit} disabled={!matchId || !content.trim()}>
            {t("admin_create")}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── View Modal ───────────────────────────────────────────────────────────────

interface ViewModalProps {
  entry: AdminEntryDto;
  onClose: () => void;
}

export const ViewEntryModal: React.FC<ViewModalProps> = ({ entry, onClose }) => {
  const { t } = useTranslation();
  return (
    <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="admin-modal entry-view-modal">
        <div className="admin-modal-header">
          <h3>{t("admin_entry_view_title")} #{entry.id}</h3>
          <button className="modal-close-btn" onClick={onClose}><FaTimes /></button>
        </div>
        <div className="admin-modal-body">
          <div className="entry-meta-row">
            <span><strong>{t("admin_entry_author_label")}:</strong> {entry.author.username} ({entry.author.status.toLowerCase()})</span>
            <span><strong>{t("admin_entry_date_label")}:</strong> {formatAdminDate(entry.createdAt)}</span>
          </div>
          {entry.match && (
            <div className="entry-meta-row">
              <span><strong>{t("admin_entry_match_label")}:</strong> {entry.match.homeTeamName} vs {entry.match.awayTeamName} — {entry.match.matchDate}</span>
            </div>
          )}
          {entry.person && (
            <div className="entry-meta-row">
              <span><strong>Kişi:</strong>{" "}
                <a href={`/kisi/${entry.person.slug}`} target="_blank" rel="noopener noreferrer" className="admin-entry-person-link">
                  {entry.person.displayName}
                </a>
              </span>
            </div>
          )}
          {entry.club && (
            <div className="entry-meta-row">
              <span><strong>Kulüp:</strong>{" "}
                <a href={`/kulup/${entry.club.slug}`} target="_blank" rel="noopener noreferrer" className="admin-entry-person-link">
                  {entry.club.shortName}
                </a>
              </span>
            </div>
          )}
          {entry.team && (
            <div className="entry-meta-row">
              <span><strong>Takım:</strong>{" "}
                <a href={`/takim/${entry.team.clubSlug}/${entry.team.sportSlug}`} target="_blank" rel="noopener noreferrer" className="admin-entry-person-link">
                  {entry.team.displayName}
                </a>
              </span>
            </div>
          )}
          <div className="entry-vote-row">
            <span className="vote-chip up"><FaChevronUp /> {entry.plusCount}</span>
            <span className="vote-chip down"><FaChevronDown /> {entry.minusCount}</span>
            <span className="vote-chip fav"><FaStar /> {entry.favCount}</span>
          </div>
          {entry.deletedAt && (
            <div className="entry-deleted-notice">
              <FaExclamationTriangle />
              {" "}{t("admin_entry_deleted_by_label")}: <strong>{entry.deletedBy?.username}</strong>
              {" — "}{formatAdminDate(entry.deletedAt)}
              {entry.deleteReason && <em>{" — "}{entry.deleteReason}</em>}
            </div>
          )}
          <div className="entry-content-box"><pre>{entry.content}</pre></div>
        </div>
        <div className="admin-modal-footer">
          <button className="modal-cancel-btn" onClick={onClose}>{t("admin_close")}</button>
        </div>
      </div>
    </div>
  );
};

// ─── Edit Modal ───────────────────────────────────────────────────────────────

interface EditModalProps {
  entry: AdminEntryDto;
  content: string;
  setContent: (v: string) => void;
  reason: string;
  setReason: (v: string) => void;
  preview: boolean;
  setPreview: (v: boolean) => void;
  draftRestored: boolean;
  onDiscardDraft: () => void;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  error: string | null;
  onClose: () => void;
  onSubmit: () => void;
}

export const EditEntryModal: React.FC<EditModalProps> = ({
  entry, content, setContent, reason, setReason,
  preview, setPreview, draftRestored, onDiscardDraft,
  textareaRef, error, onClose, onSubmit,
}) => {
  const { t } = useTranslation();
  // Admin-configurable maximum entry length — same rule the backend enforces
  const { entryMaxLength } = useSiteSettings();
  return (
    <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="admin-modal entry-edit-modal">
        <div className="admin-modal-header">
          <h3>{t("admin_entry_edit_title")} #{entry.id}</h3>
          <button className="modal-close-btn" onClick={onClose}><FaTimes /></button>
        </div>
        <div className="admin-modal-body">
          {error && <div className="modal-error">{error}</div>}
          <p style={{ margin: 0, fontSize: "var(--font-size-sm)", color: "var(--text-secondary)" }}>
            {t("admin_entry_author_label")}: <strong>{entry.author.username}</strong>
          </p>
          <div className="entry-form-group">
            <label>{t("admin_entry_form_content")}</label>
            {draftRestored && (
              <div className="epf-draft-notice">
                {t("admin_entry_draft_restored")}{" "}
                <button type="button" className="epf-draft-discard" onClick={onDiscardDraft}>
                  {t("admin_entry_draft_discard")}
                </button>
              </div>
            )}
            <div className="epf-toolbar admin-epf-toolbar">
              <EntryEditorToolbar textareaRef={textareaRef} content={content} setContent={setContent} />
              <span className="epf-char-counter">{content.length} / {entryMaxLength}</span>
            </div>
            {preview && (
              <div className="epf-preview-inline">
                <div className="epf-preview-inline-label">{t("admin_entry_preview_label")}</div>
                <div className="epf-preview-body">{renderEntryMarkup(content)}</div>
              </div>
            )}
            <textarea
              ref={textareaRef}
              className="entry-edit-textarea admin-epf-textarea"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={8}
              maxLength={entryMaxLength}
            />
            <div className="admin-epf-bottom-row">
              <div className="epf-keyboard-wrapper">
                {content.length > 0 && (
                  <button type="button" className="epf-clear-btn" title={t("admin_entry_clear")}
                    onClick={() => { setContent(""); setPreview(false); }}>
                    <RiDeleteBin6Line size={16} />
                  </button>
                )}
              </div>
              <button type="button" className="epf-preview-btn"
                onClick={() => setPreview(!preview)} disabled={!content.trim()}>
                {t("admin_entry_preview_btn")}
              </button>
            </div>
          </div>
          <div className="entry-form-group">
            <label>{t("admin_entry_edit_reason")}</label>
            <input
              type="text"
              placeholder={t("admin_entry_edit_reason_hint")}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={ADMIN_REASON_MAX_LENGTH}
            />
            <AdminCharCounter value={reason} max={ADMIN_REASON_MAX_LENGTH} />
          </div>
        </div>
        <div className="admin-modal-footer">
          <button className="modal-cancel-btn" onClick={onClose}>{t("admin_cancel")}</button>
          <button className="modal-confirm-btn" onClick={onSubmit} disabled={!content.trim()}>
            {t("admin_save")}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Delete Modal ─────────────────────────────────────────────────────────────

interface DeleteModalProps {
  entry: AdminEntryDto;
  reason: string;
  setReason: (v: string) => void;
  error: string | null;
  onClose: () => void;
  onSubmit: () => void;
}

export const DeleteEntryModal: React.FC<DeleteModalProps> = ({
  entry, reason, setReason, error, onClose, onSubmit,
}) => {
  const { t } = useTranslation();
  return (
    <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="admin-modal entry-delete-modal">
        <div className="admin-modal-header">
          <h3>{t("admin_entry_delete_title")}</h3>
          <button className="modal-close-btn" onClick={onClose}><FaTimes /></button>
        </div>
        <div className="admin-modal-body">
          {error && <div className="modal-error">{error}</div>}
          <p style={{ margin: 0 }}>
            <strong>#{entry.id}</strong> {t("admin_entry_delete_confirm")} (<strong>{entry.author.username}</strong>) {t("admin_entry_delete_confirm2")}
          </p>
          <div className="entry-form-group">
            <label>{t("admin_entry_delete_reason")}</label>
            <input
              type="text"
              placeholder={t("admin_entry_delete_reason_hint")}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={ADMIN_REASON_MAX_LENGTH}
            />
            <AdminCharCounter value={reason} max={ADMIN_REASON_MAX_LENGTH} />
          </div>
          <p className="modal-info-text">{t("admin_entry_delete_info")}</p>
        </div>
        <div className="admin-modal-footer">
          <button className="modal-cancel-btn" onClick={onClose}>{t("admin_cancel")}</button>
          <button className="modal-delete-btn" onClick={onSubmit}>{t("admin_delete")}</button>
        </div>
      </div>
    </div>
  );
};

// ─── Hard Delete Modal ────────────────────────────────────────────────────────

interface HardDeleteModalProps {
  entry: AdminEntryDto;
  error: string | null;
  onClose: () => void;
  onSubmit: () => void;
}

export const HardDeleteEntryModal: React.FC<HardDeleteModalProps> = ({
  entry, error, onClose, onSubmit,
}) => {
  const { t } = useTranslation();
  return (
    <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="admin-modal entry-hard-modal">
        <div className="admin-modal-header danger-header">
          <h3><FaExclamationTriangle /> {t("admin_entry_hard_delete_title")}</h3>
          <button className="modal-close-btn" onClick={onClose}><FaTimes /></button>
        </div>
        <div className="admin-modal-body">
          {error && <div className="modal-error">{error}</div>}
          <p className="danger-warning-text">
            {t("admin_entry_hard_delete_confirm", { id: entry.id })}
          </p>
        </div>
        <div className="admin-modal-footer">
          <button className="modal-cancel-btn" onClick={onClose}>{t("admin_cancel")}</button>
          <button className="modal-delete-btn" onClick={onSubmit}>{t("admin_permanently_delete")}</button>
        </div>
      </div>
    </div>
  );
};